import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { getCurrentPracticeId, getCurrentUserInfo } from "../lib/api";
import { db } from "../lib/db";
import { useApp } from "../context/AppContext";
import { MOCK_PORTAL_INBOX } from "../components/PatientInboxPage";
import { FaxInboxWidget } from "../components/FaxPage";
import { MessagesUnreadWidget } from "../components/MessagesPage";
// Lazy — a static import here dragged all of DocumentsPage (~364KB) into the
// MAIN bundle, defeating the code-split (found 2026-07-08).
const DocumentExpirationWidget = lazy(() =>
  import("../components/DocumentsPage").then((m) => ({ default: m.DocumentExpirationWidget })));
import { PendingIntakesDashWidget } from "../components/IntakeCapture";
import { APPT_STATUS_BADGE } from "../data/mockData";
import { getInitials, fmt12, formatDateDisplay, todayISO } from "../utils/chartHelpers";

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const fmt$ = (n) => n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── PATIENT INBOX WIDGET ─────────────────────────────────────────────────────
export function PatientInboxDashWidget({ onNavigate }) {
  const { practiceId } = useApp() ?? {};
  const [msgs, setMsgs] = useState(isDemoMode ? MOCK_PORTAL_INBOX : []);

  useEffect(() => {
    if (isDemoMode || !practiceId) return;
    db
      .from('portal_messages')
      .select('id, sender, subject, message_type, created_at, is_read, responded_at')
      .eq('practice_id', practiceId)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data, error }) => {
        if (error) { console.error('[dashboard] portal_messages error:', error); return; }
        // Map sender → patient_name so existing render code works unchanged
        setMsgs((data || []).map(m => ({ ...m, patient_name: m.sender })));
      });
  }, [practiceId]);

  const unread = msgs.filter(m => !m.is_read && m.status !== "archived").length;
  const overdue = msgs.filter(m => {
    if (m.status === "resolved" || m.status === "archived") return false;
    if (m.responded_at) return false;
    return (new Date() - new Date(m.created_at)) > 86400000;
  }).length;
  const recent = msgs.filter(m => !m.is_read && m.status !== "archived").slice(0, 3);
  const typeColors = { "post-op-concern": "#e05252", "new-symptom": "#e05252", "medication-refill": "#f0a500", "billing": "#22c55e", "appointment": "#3b82f6", "referral": "#0ea5a0", "test-results": "#a855f7", "general": "#0ea5a0" };
  const typeIcons  = { "post-op-concern": "🩹", "new-symptom": "🚨", "medication-refill": "💊", "billing": "💰", "appointment": "📅", "referral": "📋", "test-results": "🔬", "general": "🔵" };
  if (unread === 0 && overdue === 0) return null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">📨 Patient Messages</span>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto", alignItems: "center" }}>
          {overdue > 0 && <span className="badge badge-red">{overdue} overdue</span>}
          {unread  > 0 && <span className="badge badge-amber">{unread} unread</span>}
          <button className="btn btn-ghost btn-sm" onClick={() => onNavigate("patient-inbox")}>View All →</button>
        </div>
      </div>
      <div className="card-body" style={{ padding: "0 0 4px" }}>
        {recent.map(m => (
          <div key={m.id} className="tr-clickable" onClick={() => onNavigate("patient-inbox")}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontSize: 16 }}>{typeIcons[m.message_type] || "📄"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.patient_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.subject}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: typeColors[m.message_type] || "var(--teal)", background: (typeColors[m.message_type] || "#0ea5a0") + "22", padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}>
              {m.message_type?.replace("-", " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SKELETON ROW ─────────────────────────────────────────────────────────────
function Skeleton({ w = "100%", h = 14, r = 6, style = {} }) {
  return (
    <div style={{ width: w, height: h, borderRadius: r, background: "rgba(255,255,255,0.06)", ...style }} />
  );
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, accent, loading, onClick }) {
  return (
    <div
      className={`stat-card${onClick ? " stat-card-link" : ""}`}
      style={accent ? { borderColor: `rgba(${accent},0.4)` } : {}}
      onClick={onClick}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div className="stat-label">{label}</div>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
      </div>
      {loading ? (
        <Skeleton w={60} h={28} r={4} style={{ margin: "4px 0" }} />
      ) : (
        <div
          className="stat-value"
          style={accent ? { color: `rgb(${accent})` } : {}}
        >{value ?? "—"}</div>
      )}
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
function EmptyState({ icon, message, cta, onCta }) {
  return (
    <div style={{ padding: "28px 16px", textAlign: "center" }}>
      {icon && <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>}
      <div style={{ fontSize: 13, color: "var(--text-3)", marginBottom: cta ? 12 : 0 }}>{message}</div>
      {cta && (
        <button className="btn btn-ghost btn-sm" onClick={onCta}>{cta} →</button>
      )}
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function Dashboard({ onPatientSelect, onNavigate }) {
  const today     = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const todayStr  = todayISO();
  const { currentProvider: dashCurrentProvider } = useApp() ?? {};

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading]           = useState(true);
  const [pendingCosigns, setPendingCosigns] = useState(null);
  const [practiceId, setPracticeId]     = useState(null);
  const [userId, setUserId]             = useState(null);
  const [providerName, setProviderName] = useState(null); // { first, last }

  // Counts
  const [patientCount, setPatientCount]         = useState(null);
  const [notesMonthCount, setNotesMonthCount]   = useState(null);
  const [unsignedCount, setUnsignedCount]       = useState(null);
  const [ordersCount, setOrdersCount]           = useState(null);

  // Surgical pipeline
  const [surgeryOrders, setSurgeryOrders]       = useState(null);
  const [scheduledCases, setScheduledCases]     = useState(null);
  const [completedSurg, setCompletedSurg]       = useState(null);

  // Lists
  const [schedule, setSchedule]                 = useState([]);
  const [unsignedNotes, setUnsignedNotes]       = useState([]);
  const [recentPatients, setRecentPatients]     = useState([]);

  // Outcomes
  const [outcomesData, setOutcomesData]         = useState([]);

  // Performance panel
  const [perfOpen, setPerfOpen]                 = useState(false);
  const [perfNotes, setPerfNotes]               = useState([]);

  // Checked-in slide panel
  const [showCheckedInPanel, setShowCheckedInPanel] = useState(false);

  // Unposted ERAs
  const [unpostedERAs, setUnpostedERAs] = useState([]);
  const [unroutedFaxCount, setUnroutedFaxCount] = useState(0);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async (pid, uid) => {
    setLoading(true);
    try {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const monthStartStr = monthStart.toISOString();
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      // Round 1 — all parallel
      const [
        patientsRes,
        notesMonthRes,
        unsignedCountRes,
        ordersRes,
        surgOrdersRes,
        scheduledCasesRes,
        completedSurgRes,
        scheduleRes,
        unsignedNotesRes,
        recentViewedRes,
        outcomesRes,
        perfNotesRes,
      ] = await Promise.all([
        db.from("patients").select("id", { count: "exact", head: true }).eq("practice_id", pid),
        db.from("clinical_notes").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).gte("visit_date", monthStartStr),
        db.from("clinical_notes").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).in("status", ["draft", "unsigned"]),
        db.from("patient_orders").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).eq("status", "active"),
        db.from("patient_orders").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).eq("order_type", "surgery").eq("status", "active"),
        db.from("surgical_cases").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).eq("case_status", "scheduled"),
        db.from("surgical_history").select("id", { count: "exact", head: true })
          .eq("practice_id", pid).gte("surgery_date", thirtyDaysAgo),
        db.from("appointments").select("id, patient_id, appointment_type, appointment_time, status, room, provider")
          .eq("practice_id", pid).eq("appointment_date", todayStr)
          .order("appointment_time", { ascending: true }),
        db.from("clinical_notes").select("id, patient_id, note_type, visit_date, status")
          .eq("practice_id", pid).in("status", ["draft", "unsigned"])
          .order("visit_date", { ascending: false }).limit(10),
        db.from("recent_patients_viewed").select("patient_id, viewed_at")
          .eq("practice_id", pid).eq("provider_id", uid)
          .order("viewed_at", { ascending: false }).limit(5),
        db.from("pro_submissions").select("questionnaire_name, score")
          .eq("practice_id", pid).gte("submitted_at", thirtyDaysAgo),
        db.from("clinical_notes").select("note_type, icd10_codes, cpt_codes")
          .eq("practice_id", pid).gte("visit_date", monthStartStr),
      ]);

      // Counts
      setPatientCount(patientsRes.count ?? 0);
      setNotesMonthCount(notesMonthRes.count ?? 0);
      setUnsignedCount(unsignedCountRes.count ?? 0);
      setOrdersCount(ordersRes.count ?? 0);
      setSurgeryOrders(surgOrdersRes.count ?? 0);
      setScheduledCases(scheduledCasesRes.count ?? 0);
      setCompletedSurg(completedSurgRes.count ?? 0);

      // Hold raw appt rows — patient_name enriched in Round 2
      const apptRows = scheduleRes.data || [];

      // Performance notes
      setPerfNotes(perfNotesRes.data || []);

      // Outcomes snapshot — aggregate client-side
      const subs = outcomesRes.data || [];
      if (subs.length > 0) {
        const byQuestionnaire = {};
        subs.forEach(s => {
          if (!byQuestionnaire[s.questionnaire_name]) byQuestionnaire[s.questionnaire_name] = { total: 0, count: 0 };
          byQuestionnaire[s.questionnaire_name].total += s.score || 0;
          byQuestionnaire[s.questionnaire_name].count += 1;
        });
        setOutcomesData(Object.entries(byQuestionnaire).map(([name, { total, count }]) => ({
          name,
          avg: Math.round(total / count),
          count,
        })));
      } else {
        setOutcomesData([]);
      }

      // Round 2 — enrich unsigned notes + recent patients + schedule with patient data
      const noteRows = unsignedNotesRes.data || [];
      const recentViewed = recentViewedRes.data || [];

      // Only include valid UUIDs — guards against any legacy mock IDs (e.g. "P001")
      const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const allPatientIds = [
        ...new Set([
          ...noteRows.map(n => n.patient_id),
          ...recentViewed.map(r => r.patient_id),
          ...apptRows.map(a => a.patient_id),
        ].filter(id => id && uuidRe.test(id))),
      ];

      if (allPatientIds.length > 0) {
        const { data: patientRows } = await db.from("patients")
          .select("id, first_name, last_name, mrn, date_of_birth")
          .in("id", allPatientIds);

        const patMap = {};
        (patientRows || []).forEach(p => { patMap[p.id] = p; });

        setUnsignedNotes(noteRows.map(n => ({ ...n, patient: patMap[n.patient_id] || null })));
        setRecentPatients(recentViewed.map(r => ({ ...r, patient: patMap[r.patient_id] || null })).filter(r => r.patient));
        setSchedule(apptRows.map(a => {
          const pt = patMap[a.patient_id];
          return { ...a, patient_name: pt ? `${pt.first_name} ${pt.last_name}` : "Unknown" };
        }));
      } else {
        setUnsignedNotes(noteRows);
        setRecentPatients([]);
        setSchedule(apptRows);
      }
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, [todayStr]);

  useEffect(() => {
    (async () => {
      const pid = (await getCurrentPracticeId()) ?? null;
      const { uid: rawUid, email: userEmail } = await getCurrentUserInfo();
      const uid = rawUid ?? "dev";
      setPracticeId(pid);
      setUserId(uid);
      loadData(pid, uid);
      // Unposted ERA alert — separate query so failures don't block main dashboard
      if (pid) {
        db.from('payments')
          .select('id, paid_amount, payer_name, created_at, era_file')
          .eq('practice_id', pid)
          .eq('posting_status', 'unposted')
          .eq('source', 'era')
          .then(({ data }) => { if (data?.length) setUnpostedERAs(data); })
          .catch((err) => console.warn('ERA alert query failed:', err?.message || err)); // non-blocking — ERA alert is informational only
        db.from('fax_transmissions')
          .select('id', { count: 'exact', head: true })
          .eq('practice_id', pid)
          .eq('direction', 'inbound')
          .is('routed_at', null)
          .then(({ count }) => { if (count > 0) setUnroutedFaxCount(count); })
          .catch((err) => console.warn('Fax inbox query failed:', err?.message || err));
      }
      // Fetch provider name for personalized greeting + co-sign count
      if (userEmail) {
        const { data: prov } = await db
          .from('providers')
          .select('id, first_name, last_name')
          .eq('practice_id', pid)
          .eq('email', userEmail)
          .maybeSingle();
        if (prov) {
          setProviderName({ first: prov.first_name, last: prov.last_name });
          // Count pending co-sign notes assigned to this provider
          db.from('clinical_notes')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending_cosign')
            .eq('cosign_provider_id', prov.id)
            .eq('practice_id', pid)
            .then(({ count }) => setPendingCosigns(count || 0));
        }
      }
    })();
  }, [loadData]);

  // ── Re-fetch unrouted fax count when user returns to this tab ─────────────
  useEffect(() => {
    if (!practiceId) return;
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const { count } = await db
          .from('fax_transmissions')
          .select('id', { count: 'exact', head: true })
          .eq('practice_id', practiceId)
          .eq('direction', 'inbound')
          .is('routed_at', null);
        setUnroutedFaxCount(count || 0);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [practiceId]);

  // ── Greeting ───────────────────────────────────────────────────────────────
  const greeting = (() => {
    const h = new Date().getHours();
    const time = h >= 5 && h < 12 ? 'Good morning' : h >= 12 && h < 17 ? 'Good afternoon' : 'Good evening';
    if (providerName?.last)  return `${time}, Dr. ${providerName.last}`;
    if (providerName?.first) return `${time}, ${providerName.first}`;
    return `${time}, Doctor`;
  })();

  // ── Derived ────────────────────────────────────────────────────────────────
  const checkedInAppts = schedule.filter(a => a.status === "checked-in");

  // Performance data
  const notesByType = (() => {
    const counts = {};
    perfNotes.forEach(n => {
      const t = n.note_type || "Other";
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();

  const topIcd = (() => {
    const counts = {};
    perfNotes.forEach(n => {
      (n.icd10_codes || []).forEach(c => {
        const code = typeof c === "string" ? c : c?.code;
        if (code) counts[code] = (counts[code] || 0) + 1;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  })();

  const topCpt = (() => {
    const counts = {};
    perfNotes.forEach(n => {
      (n.cpt_codes || []).forEach(c => {
        const code = typeof c === "string" ? c : c?.code;
        if (code) counts[code] = (counts[code] || 0) + 1;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  })();

  const maxNoteCount = notesByType[0]?.[1] || 1;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="slide-in">
      {/* ── Checked-In Slide Panel ────────────────────────────────────────── */}
      {showCheckedInPanel && (
        <>
          <div onClick={() => setShowCheckedInPanel(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1200 }} />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 95vw)",
            background: "var(--navy-2)", borderLeft: "1px solid var(--border)",
            zIndex: 1201, display: "flex", flexDirection: "column",
            animation: "slideInRight 180ms ease",
          }}>
            <div style={{ padding: "20px 20px 14px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-1)" }}>Checked In Patients</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                </div>
                <button onClick={() => setShowCheckedInPanel(false)}
                  style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 20, lineHeight: 1 }}>✕</button>
              </div>
              {checkedInAppts.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--teal)", background: "var(--teal-lt)", borderRadius: 6, padding: "5px 10px", display: "inline-block", fontWeight: 600 }}>
                  {checkedInAppts.length} patient{checkedInAppts.length !== 1 ? "s" : ""} waiting
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              {checkedInAppts.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                  No patients currently checked in
                </div>
              ) : checkedInAppts.map(a => (
                <div key={a.id} style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, background: "var(--teal-lt)", color: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>
                      {(a.patient_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-1)", marginBottom: 2 }}>{a.patient_name || "Unknown"}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
                        {fmt12(a.appointment_time)} · {a.appointment_type || "Visit"} {a.room ? `· Room ${a.room}` : ""}
                      </div>
                      <button
                        onClick={() => { setShowCheckedInPanel(false); onNavigate("schedule"); }}
                        style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-1)", cursor: "pointer", fontSize: 11, fontWeight: 500 }}>
                        Open Schedule
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "14px 20px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
              <button onClick={() => { setShowCheckedInPanel(false); onNavigate("schedule"); }}
                style={{ width: "100%", padding: "9px", borderRadius: 7, border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 12 }}>
                View Full Schedule →
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Greeting ──────────────────────────────────────────────────────── */}
      <div className="mb-24">
        <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>{greeting}</div>
        <div className="text-muted">{today}</div>
      </div>

      {/* ── Unposted ERA Alert ───────────────────────────────────────────── */}
      {unpostedERAs.length > 0 && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div>
            <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 14 }}>
              ⚠️ {unpostedERAs.length} Unposted ERA{unpostedERAs.length > 1 ? 's' : ''}
            </span>
            <span style={{ color: 'var(--text-2)', fontSize: 13, marginLeft: 8 }}>
              {fmt$( unpostedERAs.reduce((s, e) => s + (e.paid_amount || 0), 0) )} in payments received but not posted
            </span>
          </div>
          <button
            onClick={() => onNavigate('payment-posting')}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
            Review Now →
          </button>
        </div>
      )}

      {/* ── Unrouted Fax Alert ───────────────────────────────────────────── */}
      {unroutedFaxCount > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 14 }}>
              📠 {unroutedFaxCount} unrouted fax{unroutedFaxCount > 1 ? 'es' : ''} need attention
            </span>
          </div>
          <button onClick={() => onNavigate('fax')}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(245,158,11,0.5)', background: 'transparent', color: '#f59e0b', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
            View Fax Inbox →
          </button>
        </div>
      )}

      {/* ── Stat Cards ────────────────────────────────────────────────────── */}
      <div className="grid-4 mb-24">
        <StatCard
          label="Active Patients" icon="👤"
          value={patientCount}
          sub={patientCount === 0 ? "No patients yet" : "in your practice"}
          loading={loading}
          onClick={() => onNavigate("patients")}
        />
        <StatCard
          label="Notes This Month" icon="📝"
          value={notesMonthCount}
          sub={notesMonthCount === 0 ? "No notes this month" : "signed + unsigned"}
          loading={loading}
          onClick={() => onNavigate("notes")}
        />
        <StatCard
          label="Needs Signature" icon="⚠️"
          value={unsignedCount}
          sub={unsignedCount === 0 ? "All notes signed" : `${unsignedCount} unsigned`}
          accent={unsignedCount > 0 ? "240,165,0" : null}
          loading={loading}
          onClick={() => onNavigate("notes", { filter: "pending" })}
        />
        <Suspense fallback={null}><DocumentExpirationWidget onNavigate={onNavigate} /></Suspense>
      </div>

      {/* ── Surgical Pipeline Mini-View ───────────────────────────────────── */}
      <div className="card mb-16" style={{ cursor: "pointer" }} onClick={() => onNavigate("patients")}>
        <div className="card-header">
          <span className="card-title">🔪 Surgical Pipeline</span>
          <span className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}>View Pipeline →</span>
        </div>
        <div className="card-body" style={{ display: "flex", gap: 0, padding: 0 }}>
          {/* Awaiting schedule */}
          <div style={{ flex: 1, padding: "16px 20px", borderRight: "1px solid var(--border)", textAlign: "center" }}>
            {loading ? <Skeleton w={40} h={24} r={4} style={{ margin: "0 auto 6px" }} /> : (
              <div style={{ fontSize: 24, fontWeight: 700, color: surgeryOrders > 0 ? "var(--amber)" : "var(--text-2)" }}>
                {surgeryOrders ?? "—"}
              </div>
            )}
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>Awaiting Schedule</div>
          </div>
          {/* Scheduled cases */}
          <div style={{ flex: 1, padding: "16px 20px", borderRight: "1px solid var(--border)", textAlign: "center" }}>
            {loading ? <Skeleton w={40} h={24} r={4} style={{ margin: "0 auto 6px" }} /> : (
              <div style={{ fontSize: 24, fontWeight: 700, color: scheduledCases > 0 ? "var(--teal)" : "var(--text-2)" }}>
                {scheduledCases ?? "—"}
              </div>
            )}
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>Scheduled</div>
          </div>
          {/* Completed 30d */}
          <div style={{ flex: 1, padding: "16px 20px", textAlign: "center" }}>
            {loading ? <Skeleton w={40} h={24} r={4} style={{ margin: "0 auto 6px" }} /> : (
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text-2)" }}>
                {completedSurg ?? "—"}
              </div>
            )}
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>Completed (30d)</div>
          </div>
        </div>
      </div>

      {/* ── Pending Orders stat ───────────────────────────────────────────── */}
      <div className="grid-4 mb-16" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
        <StatCard
          label="Pending Orders" icon="📋"
          value={ordersCount}
          sub={ordersCount === 0 ? "No pending orders" : "active orders"}
          loading={loading}
          onClick={() => onNavigate("pending-orders")}
        />
        <PendingIntakesDashWidget onNavigate={onNavigate} />
      </div>

      {/* ── Inbox Widgets ─────────────────────────────────────────────────── */}
      <MessagesUnreadWidget onNavigate={onNavigate} />
      <FaxInboxWidget onNavigate={onNavigate} />
      <PatientInboxDashWidget onNavigate={onNavigate} />

      {/* ── Schedule + Notes + Recent ─────────────────────────────────────── */}
      <div className="grid-2" style={{ marginBottom: 16 }}>
        {/* Today's Schedule */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Today's Schedule</span>
            {!loading && schedule.length > 0 && (
              <span className="badge badge-teal">{schedule.length} appt{schedule.length !== 1 ? "s" : ""}</span>
            )}
          </div>
          {loading ? (
            <div style={{ padding: "16px" }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <Skeleton w={52} h={14} />
                  <Skeleton w="40%" h={14} />
                  <Skeleton w={70} h={14} />
                </div>
              ))}
            </div>
          ) : schedule.length === 0 ? (
            <EmptyState icon="📅" message="No appointments scheduled today" cta="View Schedule" onCta={() => onNavigate("schedule")} />
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Time</th><th>Patient</th><th>Type</th><th>Status</th></tr></thead>
                <tbody>
                  {schedule.map(a => (
                    <tr key={a.id} className="tr-clickable" onClick={() => onNavigate("schedule")}>
                      <td><span className="font-mono text-xs">{fmt12(a.appointment_time)}</span></td>
                      <td><span className="fw-500">{a.patient_name || "—"}</span></td>
                      <td><span className={`badge ${a.appointment_type === "New Patient" ? "badge-teal" : a.appointment_type === "Post-Op" ? "badge-amber" : "badge-gray"}`}>{a.appointment_type || "Visit"}</span></td>
                      <td><span className={`badge ${APPT_STATUS_BADGE[a.status] || "badge-gray"}`}>{a.status || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="col gap-8">
          {/* Unsigned Notes */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Needs Signature</span>
              {!loading && unsignedCount > 0 && (
                <span className="badge badge-amber" style={{ marginLeft: "auto" }}>{unsignedCount} unsigned</span>
              )}
            </div>
            {loading ? (
              <div style={{ padding: "12px 16px" }}>
                {[1, 2].map(i => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                    <Skeleton w={32} h={32} r={16} />
                    <div style={{ flex: 1 }}>
                      <Skeleton w="60%" h={13} style={{ marginBottom: 6 }} />
                      <Skeleton w="40%" h={11} />
                    </div>
                  </div>
                ))}
              </div>
            ) : unsignedNotes.length === 0 ? (
              <EmptyState icon="✅" message="All notes are signed" />
            ) : (
              unsignedNotes.map(n => (
                <div key={n.id} className="tr-clickable"
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                  onClick={() => n.patient && onPatientSelect(n.patient, "notes")}
                >
                  <div className="avatar" style={{ width: 32, height: 32, fontSize: 11, background: "var(--amber-lt)", color: "var(--amber)", flexShrink: 0 }}>
                    {n.patient ? getInitials(n.patient.first_name, n.patient.last_name) : "?"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>
                      {n.patient ? `${n.patient.first_name} ${n.patient.last_name}` : `Patient ${n.patient_id?.slice(0, 8) || "—"}`}
                    </div>
                    <div className="text-xs text-muted">{n.note_type || "Note"} · {formatDateDisplay(n.visit_date)}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span className="badge badge-amber">Draft</span>
                    <span style={{ fontSize: 11, color: "var(--text-3)" }}>→</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pending Co-signs */}
          {pendingCosigns > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Pending Co-signs</span>
                <span className="badge ml-auto" style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}>{pendingCosigns} awaiting</span>
              </div>
              <div style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-2)' }}>
                {pendingCosigns} note{pendingCosigns === 1 ? '' : 's'} are waiting for your co-sign. Open the patient chart to review and co-sign.
              </div>
              <div style={{ padding: '0 16px 12px' }}>
                <button className="btn btn-primary btn-sm" style={{ background: 'rgba(139,92,246,0.2)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.4)' }} onClick={() => onNavigate("notes")}>
                  View Notes →
                </button>
              </div>
            </div>
          )}

          {/* Recent Patients */}
          <div className="card">
            <div className="card-header"><span className="card-title">Recent Patients</span></div>
            {loading ? (
              <div style={{ padding: "12px 16px" }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                    <Skeleton w={32} h={32} r={16} />
                    <div style={{ flex: 1 }}>
                      <Skeleton w="55%" h={13} style={{ marginBottom: 6 }} />
                      <Skeleton w="35%" h={11} />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentPatients.length === 0 ? (
              <EmptyState icon="👤" message="No recent patients" cta="View Patients" onCta={() => onNavigate("patients")} />
            ) : (
              recentPatients.map(r => (
                <div key={r.patient_id} className="tr-clickable"
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)" }}
                  onClick={() => onPatientSelect(r.patient)}
                >
                  <div className="avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                    {getInitials(r.patient.first_name, r.patient.last_name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{r.patient.first_name} {r.patient.last_name}</div>
                    <div className="text-xs text-muted">{r.patient.mrn}</div>
                  </div>
                  <span className="text-xs text-muted">
                    {r.viewed_at ? new Date(r.viewed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Outcomes Snapshot ─────────────────────────────────────────────── */}
      <div className="card mb-16">
        <div className="card-header">
          <span className="card-title">📈 Outcomes (Last 90 Days)</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => onNavigate("outcomes")}>View All →</button>
        </div>
        {loading ? (
          <div style={{ display: "flex", gap: 12, padding: "16px" }}>
            {[1, 2, 3].map(i => <Skeleton key={i} w={160} h={56} r={8} />)}
          </div>
        ) : outcomesData.length === 0 ? (
          <EmptyState
            icon="📊"
            message="No outcomes data yet — send your first questionnaire to get started"
            cta="Send Questionnaire"
            onCta={() => onNavigate("outcomes")}
          />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "16px" }}>
            {outcomesData.map(o => (
              <div key={o.name} style={{ background: "var(--navy-3)", borderRadius: 8, padding: "12px 16px", minWidth: 140 }}>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>{o.name}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--teal)" }}>{o.avg}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{o.count} submission{o.count !== 1 ? "s" : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Quick Actions ─────────────────────────────────────────────────── */}
      <div className="card mb-16">
        <div className="card-header"><span className="card-title">Quick Actions</span></div>
        <div className="card-body" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => onNavigate("new-patient")}>+ New Patient Registration</button>
          <button className="btn btn-secondary" onClick={() => onNavigate("schedule")}>+ Schedule Appointment</button>
          <button className="btn btn-secondary" onClick={() => onNavigate("transcribe")}>+ Dictate Note</button>
          <button className="btn btn-secondary" onClick={() => onNavigate("outcomes")}>Send PRO Questionnaire</button>
          <button className="btn btn-secondary" onClick={() => onNavigate("patients")}>View Surgical Pipeline</button>
        </div>
      </div>

      {/* ── My Performance ────────────────────────────────────────────────── */}
      <div className="card mb-16">
        <div className="card-header" style={{ cursor: "pointer" }} onClick={() => setPerfOpen(o => !o)}>
          <span className="card-title">📊 My Performance — This Month</span>
          <span style={{ marginLeft: "auto", color: "var(--text-3)", fontSize: 16 }}>{perfOpen ? "▲" : "▼"}</span>
        </div>
        {perfOpen && (
          <div className="card-body">
            {loading ? (
              <div style={{ padding: "8px 0" }}>
                <Skeleton w="100%" h={14} style={{ marginBottom: 8 }} />
                <Skeleton w="80%" h={14} style={{ marginBottom: 8 }} />
                <Skeleton w="60%" h={14} />
              </div>
            ) : perfNotes.length === 0 ? (
              <EmptyState icon="📝" message="No notes this month yet" />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
                {/* Notes by Type */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Notes by Type</div>
                  {notesByType.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>No note type data</div>
                  ) : notesByType.map(([type, count]) => (
                    <div key={type} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color: "var(--text-2)" }}>{type}</span>
                        <span style={{ color: "var(--text-1)", fontWeight: 600 }}>{count}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: "var(--navy-4)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(count / maxNoteCount) * 100}%`, background: "var(--teal)", borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Top Diagnoses */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Top Diagnoses</div>
                  {topIcd.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>No diagnosis codes this month</div>
                  ) : topIcd.map(([code, count], i) => (
                    <div key={code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontSize: 11, color: "var(--text-3)", width: 16, flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--teal)", flex: 1 }}>{code}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)" }}>{count}×</span>
                    </div>
                  ))}
                </div>

                {/* Top Procedures */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Top Procedures</div>
                  {topCpt.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>No procedure codes this month</div>
                  ) : topCpt.map(([code, count], i) => (
                    <div key={code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontSize: 11, color: "var(--text-3)", width: 16, flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--amber)", flex: 1 }}>{code}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)" }}>{count}×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
